/**
 * Embed'ets indgang: knappen, boblen og ingenting andet.
 *
 * Det her er den fil, der lander på hver eneste sidevisning, så den er skrevet
 * uden React med vilje. React følger først med panelet, når nogen klikker —
 * samme valg som den oprindelige widget traf med `next/dynamic`, og af samme
 * grund: chatten må ikke koste noget på en forside, folk kom for at læse.
 *
 * Alt tegnes i en shadow root. Det er ikke pænhed, det er en forudsætning: et
 * embed lander på sider, hvis CSS vi ikke har set, og en `button { width:100% }`
 * et fremmed sted må ikke kunne smadre knappen. Omvendt slipper vores egen CSS
 * heller ikke ud.
 *
 * Shadow root og IKKE en iframe, fordi en iframe ville flytte chatten til en
 * anden origin — og dermed efterlade al gemt samtalehistorik på den gamle.
 */

import { API } from "./api";
// Rent typeimport: det forsvinder ved bygningen, så knappen trækker IKKE
// panelet (og dermed React) med ind i sin egen fil.
import type { ChatKonfig } from "./panel/ChatPanel";

const HOST_TAG = "cleanren-chat";
// Boblen ved knappen kan lukkes. Så bliver den væk resten af besøget — ikke
// for altid: næste gang kan den besøgende have et nyt spørgsmål.
const BOBLE_LUKKET = "cleanren-chat-boble-lukket";
// Boblen kommer lidt efter knappen. Ellers dukker to ting op samtidig, og
// den besøgende har ikke nået at se siden de kom for.
const BOBLE_EFTER_MS = 2500;
const STATUS_FOERSTE_MS = 1200;
const STATUS_INTERVAL_MS = 300_000;

/* Knappens og boblens CSS, uændret fra den oprindelige widget. Den stod i
   <style jsx>, som gav hver klasse et hash-suffiks for at undgå sammenstød med
   resten af siden. Her er scopet shadow-roden selv, og så er scopingen
   overflødig: reglerne kan hverken ramme eller rammes udefra. */
const KNAP_CSS = `
:host {
  /* Værten er et tomt element; alt indeni er fixed. Uden den her kunne en
     side med "* { box-sizing }" eller egne div-regler give den højde. */
  all: initial;
  font: inherit;

  /* ── Hvor lavt chatten må sidde ─────────────────────────────────
     Standardværdierne er cleanren.dk's, uændrede. Den side har på
     telefonen to faste lag i bunden — en navigationsbjælke på 64 px og et
     ring/skriv-banner oveni — og de 128 px er målt til at rydde dem begge.

     En anden side har ikke nødvendigvis de lag. Uden en bundnavigation
     svæver knappen 128 px oppe med tomt under sig, og det ligner en fejl.
     Derfor er afstandene variabler, som siden selv kan sætte på værten —
     en regel i sidens eget stilark vinder over :host:

       cleanren-chat { --chat-bund: 20px; --chat-bund-boble: 86px; }

     De hører til SIDEN og ikke til chatten: kun siden ved, hvad der ellers
     ligger i dens bund. */
  --chat-bund:       calc(128px + env(safe-area-inset-bottom));
  --chat-bund-boble: calc(190px + env(safe-area-inset-bottom));
}
@media (min-width: 768px) {
  /* På større skærme har cleanren.dk ingen bundnavigation — den er
     md:hidden — så chatten går ned i hjørnet. */
  :host {
    --chat-bund:       24px;
    --chat-bund-boble: 86px;
  }
}
.chatboble {
  position: fixed;
  right: 20px;
  bottom: var(--chat-bund-boble);
  z-index: 200;
  max-width: min(290px, calc(100vw - 40px));
  animation: bobleind 0.3s ease-out;
}
@media (min-width: 768px) {
  .chatboble { right: 24px; }
}
.chatboble-tekst {
  display: block;
  width: 100%;
  padding: 13px 34px 13px 15px;
  border: 1px solid #e0e9f1;
  border-radius: 16px 16px 5px 16px;
  background: #fff;
  box-shadow: 0 8px 26px rgba(15, 43, 74, 0.18);
  color: #23303f;
  font: inherit;
  font-size: 14.5px;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;
}
.chatboble-tekst:hover { border-color: #c8dceb; }
.chatboble-tekst:focus-visible,
.chatboble-luk:focus-visible {
  outline: 3px solid #9ecbea;
  outline-offset: 2px;
}
.chatboble-luk {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #eef3f8;
  color: #52677c;
  cursor: pointer;
}
.chatboble-luk:hover { background: #e0e9f1; }
@keyframes bobleind {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .chatboble { animation: none; }
}

/* Den grønne prik sidder i knappens hjørne og pulser roligt. Den er
   aria-hidden: den gentager ikke noget, en skærmlæser mangler, og en
   blinkende "live"-melding uden handling ville bare støje. */
.chatknap-ikon {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
}
.chatknap-live {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3ddc84;
  box-shadow: 0 0 0 2px #1a5e90;
}
.chatknap-live::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: #4ade80;
  animation: chatpuls 2s ease-out infinite;
}
@keyframes chatpuls {
  0%   { transform: scale(1);   opacity: 0.55; }
  70%  { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .chatknap-live::after { animation: none; }
}

.chatknap {
  position: fixed;
  right: 20px;
  /* Se --chat-bund på :host: hvor lavt knappen må sidde, afhænger af hvad
     siden ellers har liggende i bunden af skærmen. */
  bottom: var(--chat-bund);
  z-index: 200;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 13px 18px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #2176ae, #0f4c81);
  box-shadow: 0 6px 22px rgba(15, 43, 74, 0.28);
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.chatknap:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(15, 43, 74, 0.34);
}
.chatknap:focus-visible {
  outline: 3px solid #9ecbea;
  outline-offset: 3px;
}
@media (min-width: 768px) {
  .chatknap { right: 24px; }
}
/* På små skærme er der ikke plads til både ikon og tekst ved siden
   af det der i forvejen står i bunden. */
@media (max-width: 420px) {
  .chatknap { padding: 13px; }
  .chatknap-tekst { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .chatknap { transition: none; }
  .chatknap:hover { transform: none; }
}
`;

const IKON_HEADSET = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M4 14v-2a8 8 0 0 1 16 0v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <path d="M20 15.5v1A3.5 3.5 0 0 1 16.5 20H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <rect x="2" y="13" width="4" height="6" rx="1.6" stroke="currentColor" stroke-width="2" />
  <rect x="18" y="13" width="4" height="6" rx="1.6" stroke="currentColor" stroke-width="2" />
</svg>`;

const IKON_KRYDS = `
<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" />
</svg>`;

/* sessionStorage kaster af sig selv i private vinduer og med cookies slået
   fra. Intet herinde må chatten stå og falde med. */
function husk(noegle: string): boolean {
  try { return Boolean(sessionStorage.getItem(noegle)); } catch { return false; }
}
function gem(noegle: string): void {
  try { sessionStorage.setItem(noegle, "1"); } catch { /* privat vindue */ }
}

/* Panelet styres udefra med tre ord, og de holdes bevidst adskilt:
   `aabn` er en HANDLING fra den besøgende, `opdater` er nye oplysninger fra
   serveren. Blandes de sammen, ville statusopslaget hvert femte minut lukke
   chatten op igen for en, der lige havde lukket den. */
type Panel = {
  aabn(): void;
  opdater(props: { live: boolean; konfig: ChatKonfig }): void;
};

function start(): void {
  // To scripttags på samme side (en glemt kopi i en skabelon) må ikke give to
  // knapper oven i hinanden.
  if (document.querySelector(HOST_TAG)) return;

  const vaert = document.createElement(HOST_TAG);
  const rod = vaert.attachShadow({ mode: "open" });
  const stil = document.createElement("style");
  stil.textContent = KNAP_CSS;
  rod.appendChild(stil);
  document.body.appendChild(vaert);

  let aktiv = false;
  let live = false;
  let hilsen = "";
  let konfig: ChatKonfig = {};
  let aaben = false;
  let panel: Panel | null = null;
  let henterPanel = false;
  let bobleVist = false;
  let bobleTimer: number | undefined;

  const knap = document.createElement("button");
  knap.type = "button";
  knap.className = "chatknap";
  knap.setAttribute("aria-label", "Åbn chat med Clean Rens kundeservice");
  knap.hidden = true;
  // Headset frem for en taleboble: en boble kan læses som «skriv en besked og
  // vent», mens et headset siger kundeservice. Prikken sidder PÅ ikonet som en
  // tilstedeværelsesmarkering — samme sprog som et profilbillede i en chat-app.
  knap.innerHTML =
    `<span class="chatknap-ikon">${IKON_HEADSET}</span>` +
    `<span class="chatknap-tekst">Chat med os</span>`;
  const ikon = knap.querySelector(".chatknap-ikon") as HTMLElement;
  rod.appendChild(knap);

  let boble: HTMLDivElement | null = null;

  function tegnPrik(): void {
    const har = ikon.querySelector(".chatknap-live");
    if (live && !har) {
      const prik = document.createElement("span");
      prik.className = "chatknap-live";
      prik.setAttribute("aria-hidden", "true");
      ikon.appendChild(prik);
    } else if (!live && har) {
      har.remove();
    }
  }

  function fjernBoble(husket: boolean): void {
    if (bobleTimer) { clearTimeout(bobleTimer); bobleTimer = undefined; }
    boble?.remove();
    boble = null;
    if (husket) gem(BOBLE_LUKKET);
  }

  function tegnBoble(): void {
    if (boble || aaben || !hilsen) return;
    boble = document.createElement("div");
    boble.className = "chatboble";
    boble.setAttribute("role", "status");
    // Hilsenen står ved knappen, før chatten er åbnet. Et klik på den åbner
    // chatten — det er den samme invitation som knappen.
    const tekst = document.createElement("button");
    tekst.type = "button";
    tekst.className = "chatboble-tekst";
    tekst.textContent = hilsen;
    tekst.addEventListener("click", aabn);
    const luk = document.createElement("button");
    luk.type = "button";
    luk.className = "chatboble-luk";
    luk.setAttribute("aria-label", "Skjul beskeden");
    luk.innerHTML = IKON_KRYDS;
    luk.addEventListener("click", () => fjernBoble(true));
    boble.append(tekst, luk);
    rod.appendChild(boble);
  }

  function maaskeBoble(): void {
    // Boblen vises én gang pr. besøg, og aldrig for den der allerede har
    // åbnet chatten eller lukket boblen.
    if (bobleVist || !aktiv || !hilsen || aaben || panel) return;
    if (husk(BOBLE_LUKKET)) return;
    bobleVist = true;
    bobleTimer = window.setTimeout(tegnBoble, BOBLE_EFTER_MS);
  }

  async function aabn(): Promise<void> {
    fjernBoble(true);
    aaben = true;
    knap.hidden = true;
    if (panel) { panel.aabn(); return; }
    if (henterPanel) return;
    henterPanel = true;
    try {
      // Her — og kun her — hentes React og selve chatten. Stien slås op mod
      // widget.js's egen url, så den finder hjem uanset hvilken side den
      // kører på.
      const mod = await import("./panel/monter");
      panel = mod.monter(rod, {
        live,
        konfig,
        luk: () => {
          aaben = false;
          knap.hidden = false;
        },
      });
      panel.aabn();
    } catch (e) {
      // Kan panelet ikke hentes, skal knappen tilbage — ellers står den
      // besøgende med en chat, der forsvandt ved et klik.
      aaben = false;
      knap.hidden = false;
      console.error("[cleanren-chat] panelet kunne ikke hentes", e);
    } finally {
      henterPanel = false;
    }
  }

  knap.addEventListener("click", aabn);

  /* Prikken afgøres af SERVEREN og ikke af browseren. Tidsrummet kan ændres i
     admin, og en besøgendes eget ur eller tidszone må ikke kunne tænde den,
     når kontoret er lukket. */
  let afbrudt = false;
  async function hentStatus(): Promise<void> {
    try {
      const r = await fetch(`${API}/status`);
      if (!r.ok || afbrudt) return;
      const d = await r.json();
      aktiv = Boolean(d.aktiv);
      live = Boolean(d.live);
      if (typeof d.hilsen === "string") hilsen = d.hilsen;
      konfig = {
        velkomst: typeof d.velkomst === "string" ? d.velkomst : undefined,
        forslag: Array.isArray(d.forslag) ? d.forslag : undefined,
        captchaV3: typeof d.captcha_v3 === "string" ? d.captcha_v3 : "",
      };
      // Findes chatten ikke på serveren (CHAT_ENABLED er slået fra), tegnes
      // knappen slet ikke. Så kan den slukkes uden en udrulning.
      knap.hidden = !aktiv || aaben;
      if (!aktiv) fjernBoble(false);
      tegnPrik();
      panel?.opdater({ live, konfig });
      maaskeBoble();
    } catch {
      /* ingen forbindelse — knappen bliver væk, og den besøgende møder ikke
         en chat, der ikke kan svare */
    }
  }

  // Venter til siden er tegnet færdig — det er en chat, ikke noget forsiden
  // skal måles på.
  setTimeout(hentStatus, STATUS_FOERSTE_MS);
  // Står fanen åben hen over lukketid, skal prikken slukke af sig selv i
  // stedet for at love et menneske, der er gået hjem.
  setInterval(hentStatus, STATUS_INTERVAL_MS);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
